/**
 * @fileoverview Shared list of selectable document fonts
 * @module ui/composed/editor/font-options
 *
 * Purpose:
 * - Single source of truth for the font dropdown in the TipTap document
 *   editor toolbar and the org-level branding font picker.
 *
 * Used by:
 * - src/ui/composed/editor/template-editor.tsx (FontFamilyDropdown)
 * - src/routes/_app/_auth/dashboard/_layout.settings.branding.tsx
 *   (EditorPreferencesSection font select)
 *
 * Notes:
 * - The CSS strings in `value` are stored verbatim on document marks
 *   (TipTap `setFontFamily`) and on `brandingProfiles.editorFontFamily`,
 *   so they double as DOCX/PDF export font stacks.
 * - Webfont loading for Roboto and Playfair Display is configured in
 *   index.html (Google Fonts <link>). Aptos and Calibri are proprietary
 *   Microsoft typefaces installed locally via Microsoft 365; their stacks
 *   include sensible fallbacks (Segoe UI / sans-serif) for non-Office
 *   machines.
 */

export type FontOption = {
  readonly label: string;
  readonly value: string;
};

export const DOCUMENT_FONT_OPTIONS: readonly FontOption[] = [
  { label: "Default", value: "" },
  { label: "Aptos", value: "'Aptos', 'Segoe UI', sans-serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Calibri", value: "Calibri, sans-serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Garamond", value: "Garamond, serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
  { label: "Playfair Display", value: "'Playfair Display', Georgia, serif" },
  { label: "Roboto", value: "'Roboto', Arial, sans-serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
] as const;

/**
 * Shared list of selectable document font sizes.
 *
 * Split out of `template-editor.tsx` (react-refresh/only-export-components).
 * Used by the TipTap document editor toolbar and the org-level branding
 * font-size picker.
 */
export const FONT_SIZES = [
  { label: "Default", value: "" },
  { label: "8pt", value: "8pt" },
  { label: "9pt", value: "9pt" },
  { label: "10pt", value: "10pt" },
  { label: "11pt", value: "11pt" },
  { label: "12pt", value: "12pt" },
  { label: "14pt", value: "14pt" },
  { label: "16pt", value: "16pt" },
  { label: "18pt", value: "18pt" },
  { label: "20pt", value: "20pt" },
  { label: "24pt", value: "24pt" },
  { label: "28pt", value: "28pt" },
  { label: "36pt", value: "36pt" },
] as const;
